import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import BrandTable from '../components/brand/BrandTable';
import BrandForm from '../components/brand/BrandForm';
import BrandService from '../services/brandService';
import { Brand } from '../types/Brand';

const BrandMaintenancePage: React.FC = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const data = await BrandService.getAllBrands();
      setBrands(data);
      setError(null);
    } catch (err) {
      setError('Error loading brands. Please try again later.');
      console.error('Error fetching brands:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const handleCreateBrand = () => {
    setEditingBrand(null);
    setIsFormOpen(true);
  };

  const handleEditBrand = (brand: Brand) => {
    setEditingBrand(brand);
    setIsFormOpen(true);
  };

  const handleDeleteBrand = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) {
      return;
    }

    try {
      await BrandService.deleteBrand(id);
      // Refresh the brand list
      fetchBrands();
    } catch (err: any) {
      setError('Error deleting brand. Please try again.');
      console.error('Error deleting brand:', err);
    }
  };

  const handleFormSubmit = async (brand: Brand) => {
    try {
      if (editingBrand) {
        // Update existing brand
        await BrandService.updateBrand(editingBrand.id!, brand);
      } else {
        // Create new brand
        await BrandService.createBrand(brand);
      }

      // Close the form and refresh brands
      setIsFormOpen(false);
      setEditingBrand(null);
      fetchBrands();
    } catch (err: any) {
      setError(err.message || 'Error saving brand. Please try again.');
      console.error('Error saving brand:', err);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingBrand(null);
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Brand Maintenance</span>
          </div>

          <div className="content-container">
            <div className="page-header">
              <h1>Brand Maintenance</h1>
              <button 
                className="primary-button"
                onClick={handleCreateBrand}
                disabled={isFormOpen}
              >
                Add New Brand
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {isFormOpen ? (
              <BrandForm 
                brand={editingBrand}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            ) : (
              <BrandTable
                brands={brands}
                loading={loading}
                onEdit={handleEditBrand}
                onDelete={handleDeleteBrand}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandMaintenancePage; 